/**
 * CONTRACT LIFECYCLE — the client holds the contract row id and echoes it back.
 *
 * `POST /addContract` used to infer WHICH contract row to update from the `latest`
 * flag. That flag carries no uniqueness constraint and the invariant is provably
 * broken in production, so "the latest row" is not a thing that reliably exists.
 * The backend now updates the row the CLIENT names, and returns the id of the row
 * it wrote so the client can name it on the next submit.
 *
 * Two rules this module exists to enforce:
 *
 *  1. ABSENT MEANS ABSENT. Omitting `injection_contract_id` / `offtake_contract_id`
 *     stays valid — the backend falls back to deriving the row itself. So nothing
 *     here ever emits `null` or `""` for an id it does not hold; it emits no key.
 *  2. ONLY FOR A DIRECTION BEING ACTED ON. An id sent for the other leg is a 400
 *     (`CONTRACT_ID_DIRECTION_MISMATCH`) by design, not a silently-ignored field.
 */

export type CommodityDirection = "injection" | "offtake";

/** The ids currently held, per direction. A direction with no id has NO key. */
export type ContractIdMap = { injection?: number; offtake?: number };

/**
 * localStorage key. Deliberately a sibling of `installationNumbers`: the id is only
 * meaningful for the EAN + company the installation numbers describe, so it is
 * written, read and — critically — CLEARED at exactly the same points.
 */
export const CONTRACT_IDS_STORAGE_KEY = "contractIds";

const CONTRACT_ID_FIELD: Record<CommodityDirection, string> = {
  injection: "injection_contract_id",
  offtake: "offtake_contract_id",
};

/**
 * A contract id, or `null` when the value is not one.
 *
 * Accepts a numeric string as well as a number: the id crosses a JSON boundary out
 * of a MySQL integer column, and a driver that renders large integers as strings
 * must not silently cost us the round-trip. Anything else — 0, negatives,
 * fractions, `null` — is not an id.
 */
export const toContractId = (value: unknown): number | null => {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * The ids `POST /addContract` reports back.
 *
 * The handler answers `{ response: {...} }`, but every other reader in this codebase
 * tolerates the unwrapped shape too, so this one does as well. `contracts.<direction>`
 * is `null` for a leg the request did not act on, and `contract_id` is non-null
 * exactly when the write happened — so a direction with no write yields no key.
 */
export const readAddContractIds = (data: unknown): ContractIdMap => {
  const body = (data ?? {}) as { response?: Record<string, unknown> } & Record<string, unknown>;
  const inner = (body.response ?? body) as { contracts?: unknown };
  const contracts = (inner?.contracts ?? {}) as Record<string, { contract_id?: unknown } | null>;

  const ids: ContractIdMap = {};
  for (const direction of ["injection", "offtake"] as CommodityDirection[]) {
    const id = toContractId(contracts?.[direction]?.contract_id);
    if (id !== null) ids[direction] = id;
  }
  return ids;
};

/**
 * Merge strategy identical to the one `installationNumbers` uses: a direction the
 * new response said nothing about keeps whatever it already held. A single-direction
 * submit must not wipe the other leg's id.
 */
export const mergeContractIds = (existing: ContractIdMap, incoming: ContractIdMap): ContractIdMap => ({
  ...existing,
  ...(incoming.injection !== undefined && { injection: incoming.injection }),
  ...(incoming.offtake !== undefined && { offtake: incoming.offtake }),
});

/** The directions a given `contract_type` actually writes a contract row for. */
export const directionsActedOn = (
  contractType: "injection" | "offtake" | "combined"
): CommodityDirection[] => (contractType === "combined" ? ["injection", "offtake"] : [contractType]);

/**
 * The `*_contract_id` fields to spread onto an `/addContract` payload.
 *
 * Returns ONLY the keys for which an id is genuinely held AND whose direction this
 * submit is acting on. Everything else is omitted — never `null`, never `""`.
 */
export const buildContractIdFields = (
  ids: ContractIdMap,
  directions: readonly CommodityDirection[]
): Record<string, number> => {
  const fields: Record<string, number> = {};
  for (const direction of directions) {
    const id = ids[direction];
    if (id !== undefined) fields[CONTRACT_ID_FIELD[direction]] = id;
  }
  return fields;
};

/** Reads the held ids out of localStorage. Never throws on corrupt JSON. */
export const readStoredContractIds = (): ContractIdMap => {
  try {
    const stored = localStorage.getItem(CONTRACT_IDS_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored) as Record<string, unknown>;
    const ids: ContractIdMap = {};
    for (const direction of ["injection", "offtake"] as CommodityDirection[]) {
      const id = toContractId(parsed?.[direction]);
      if (id !== null) ids[direction] = id;
    }
    return ids;
  } catch (e) {
    console.error("Failed to parse stored contract ids:", e);
    return {};
  }
};

/** Persists the held ids, or removes the key entirely when nothing is held. */
export const writeStoredContractIds = (ids: ContractIdMap): void => {
  if (ids.injection === undefined && ids.offtake === undefined) {
    localStorage.removeItem(CONTRACT_IDS_STORAGE_KEY);
    return;
  }
  localStorage.setItem(CONTRACT_IDS_STORAGE_KEY, JSON.stringify(ids));
};

/** Drops the held ids. Called wherever `installationNumbers` is dropped. */
export const clearStoredContractIds = (): void => localStorage.removeItem(CONTRACT_IDS_STORAGE_KEY);

/**
 * The contracts_flex id a PV/BESS onboarding `/generate` call answers with. `contract_id`
 * is the documented field; `id` is the legacy read kept as a fallback.
 */
export const readGeneratedContractId = (data: unknown): number | null => {
  if (!data || typeof data !== "object") return null;
  const body = data as { contract_id?: unknown; id?: unknown };
  return toContractId(body.contract_id ?? body.id);
};
